'use client';

// @boty/ui - ChatInput component
// WhatsApp-style input field for sending chat messages
// Translation-agnostic: accepts placeholder and aria labels as props
// Story 4.5: Mobile-Optimized Bot Interface - touch-friendly

import { useState, useCallback, useRef, useEffect, type KeyboardEvent } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import SendIcon from '@mui/icons-material/Send';
import { useTheme, alpha } from '@mui/material/styles';

export interface ChatInputProps {
  /** Callback when message is sent */
  onSend: (message: string) => void;
  /** Placeholder text for the input */
  placeholder: string;
  /** Aria label for send button */
  sendAriaLabel?: string;
  /** Whether input should be disabled */
  disabled?: boolean;
  /** Whether a message is currently being sent */
  isLoading?: boolean;
  /** Maximum message length (default: 500) */
  maxLength?: number;
  /** Show counter when length exceeds this threshold (default: 400) */
  showCounterThreshold?: number;
  /** Character count format string, e.g., "{count}/{max}" */
  characterCountFormat?: string;
  /** Initial value for the input */
  initialValue?: string;
  /** Callback when input value changes */
  onChange?: (value: string) => void;
}

/**
 * ChatInput - WhatsApp-style input component for sending chat messages
 *
 * Features:
 * - Input + Send button layout (WhatsApp style)
 * - 16px font on mobile to prevent iOS zoom
 * - Multiline support (max 4 rows auto-expand)
 * - Enter to send, Shift+Enter for newline
 * - Primary color Send button, disabled when empty
 * - Disable during loading
 * - Auto-focus on mount
 * - Clear after sending
 * - Character limit with optional counter display
 * - RTL-aware send icon direction
 *
 * @example
 * ```tsx
 * <ChatInput
 *   onSend={(msg) => console.log(msg)}
 *   placeholder="Type a message..."
 *   sendAriaLabel="Send message"
 *   maxLength={500}
 * />
 * ```
 */
export function ChatInput({
  onSend,
  placeholder,
  sendAriaLabel = 'Send',
  disabled = false,
  isLoading = false,
  maxLength = 500,
  showCounterThreshold = 400,
  characterCountFormat = '{count}/{max}',
  initialValue = '',
  onChange,
}: ChatInputProps) {
  const theme = useTheme();
  const isRtl = theme.direction === 'rtl';
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync value when initialValue changes (e.g. suggested questions tapped)
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Focus on mount and re-focus after loading completes (bot response received)
  useEffect(() => {
    if (!disabled && !isLoading && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled, isLoading]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (newValue.length <= maxLength) {
        setValue(newValue);
        onChange?.(newValue);
      }
    },
    [maxLength, onChange]
  );

  const handleSend = useCallback(() => {
    const trimmedValue = value.trim();
    if (trimmedValue && !disabled && !isLoading) {
      onSend(trimmedValue);
      setValue('');
    }
  }, [value, disabled, isLoading, onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      // Enter without Shift sends the message
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const isDisabled = disabled || isLoading;
  const canSend = value.trim().length > 0 && !isDisabled;
  const showCounter = value.length > showCounterThreshold;
  const isNearLimit = value.length > maxLength - 50;

  const formattedCount = characterCountFormat
    .replace('{count}', String(value.length))
    .replace('{max}', String(maxLength));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isDisabled}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              // Light primary-tinted fill so the box stands out from the white bar at rest
              backgroundColor: (t) => alpha(t.palette.primary.main, 0.06),
              boxShadow: (t) => `0 1px 4px ${alpha(t.palette.primary.main, 0.12)}`,
              transition: 'box-shadow 0.2s, background-color 0.2s',
              // Emphasized primary border to draw attention to the input
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: (t) => alpha(t.palette.primary.main, 0.5),
                borderWidth: 2,
              },
              '&:hover': {
                backgroundColor: (t) => alpha(t.palette.primary.main, 0.1),
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              // On focus: clean white fill plus a soft focus ring for extra prominence
              '&.Mui-focused': {
                backgroundColor: 'background.paper',
                boxShadow: (t) => `0 0 0 4px ${alpha(t.palette.primary.main, 0.15)}`,
              },
            },
            '& .MuiInputBase-input': {
              // 16px font prevents iOS zoom on focus; slightly larger for emphasis
              fontSize: { xs: '16px', md: '15px' },
              py: 0.5,
            },
          }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!canSend}
          aria-label={sendAriaLabel}
          sx={{
            backgroundColor: canSend ? 'primary.main' : 'grey.200',
            color: canSend ? 'white' : 'grey.400',
            width: 56,
            height: 56,
            flexShrink: 0,
            boxShadow: canSend
              ? (t) => `0 2px 8px ${alpha(t.palette.primary.main, 0.4)}`
              : 'none',
            // Touch-friendly: only show hover on pointer devices
            '@media (hover: hover)': {
              '&:hover': {
                backgroundColor: canSend ? 'primary.dark' : 'grey.200',
              },
            },
            // Active state for touch feedback
            '&:active': {
              transform: 'scale(0.95)',
            },
            // Prevent double-tap zoom on mobile
            touchAction: 'manipulation',
            '&.Mui-disabled': {
              backgroundColor: 'grey.200',
              color: 'grey.400',
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <SendIcon sx={{ fontSize: 26, transform: isRtl ? 'scaleX(-1)' : 'none' }} />
          )}
        </IconButton>
      </Box>
      {showCounter && (
        <Typography
          variant="caption"
          sx={{
            textAlign: 'end',
            color: isNearLimit ? 'warning.main' : 'text.secondary',
            px: 1,
          }}
        >
          {formattedCount}
        </Typography>
      )}
    </Box>
  );
}

ChatInput.displayName = 'ChatInput';
